import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  CoatingBodySize,
  CoatingCourseItem,
  CoatingOptionItem,
  CoatingPolishOption,
} from '@/types/api/coatingApi';

const validationSchema = yup.object().shape({
  tStockCarId: yup.number().required(),
  bodySize: yup.number().oneOf([1, 2]).required(),
  polishOption: yup.number().oneOf([0, 1, 2]).required(),
  coatingItemCode: yup.number().required(),
  optionItemCodes: yup.array().of(yup.number()),
  bodyColor: yup.string(),
  pastCoatingHistory: yup.string(),
});

export interface CoatingStep1FormData {
  tStockCarId?: number;
  bodySize?: CoatingBodySize;
  polishOption?: CoatingPolishOption;
  coatingItemCode?: number;
  optionItemCodes: number[];
  bodyColor: string;
  pastCoatingHistory: string;
}

export const coatingStep1InitialState: CoatingStep1FormData = {
  tStockCarId: undefined,
  bodySize: undefined,
  polishOption: undefined,
  coatingItemCode: undefined,
  optionItemCodes: [],
  bodyColor: '',
  pastCoatingHistory: '',
};

/**
 * コーティングStep1フック
 * コース・オプション選択 + 価格マトリクス連動
 */
export const useCoatingStep1 = (
  initialState: CoatingStep1FormData,
  courseItems: CoatingCourseItem[],
  optionItems: CoatingOptionItem[],
) => {
  const [fee, setFee] = useState(0);
  const [workingMinutes, setWorkingMinutes] = useState(0);
  const formMethods = useForm<CoatingStep1FormData>({
    defaultValues: initialState,
    resolver: yupResolver(validationSchema) as any,
    mode: 'onChange',
  });

  const { watch, setValue, trigger, reset } = formMethods;
  const coatingItemCode = watch('coatingItemCode');
  const optionItemCodes = watch('optionItemCodes');
  const bodySize = watch('bodySize');
  const polishOption = watch('polishOption');

  // 初期状態リセット
  useEffect(() => {
    if (initialState.coatingItemCode) {
      reset(initialState);
    }
  }, [initialState, reset]);

  // bodySize / polishOption 変更時にコース選択をリセット
  useEffect(() => {
    if (coatingItemCode) {
      setValue('coatingItemCode', undefined);
      trigger('coatingItemCode');
    }
    // bodySize, polishOption の変更のみを監視
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodySize, polishOption]);

  // 料金計算（価格マトリクス連動）
  useEffect(() => {
    let coursePrice = 0;
    let courseMinutes = 0;

    if (
      coatingItemCode &&
      bodySize !== undefined &&
      polishOption !== undefined
    ) {
      const course = courseItems.find((c) => c.code === coatingItemCode);
      if (course) {
        const matrixEntry = course.price_matrix.find(
          (m) => m.body_size === bodySize && m.polish_option === polishOption,
        );
        if (matrixEntry) {
          coursePrice = matrixEntry.price;
          courseMinutes = matrixEntry.working_minutes;
          // キャンペーン割引適用
          if (course.discount_info?.length) {
            const discount = course.discount_info[0];
            coursePrice = Math.max(0, coursePrice - discount.discount_amount);
          }
        }
      }
    }

    const selectedOptions = optionItems.filter((c) =>
      optionItemCodes.includes(c.code),
    );
    const optionPrice = selectedOptions.reduce((current, item) => {
      const price = !!item?.discount_info?.length
        ? item.discount_info[0].discounted_price
        : item?.price;
      return current + price;
    }, 0);
    const optionMinutes = selectedOptions.reduce(
      (current, item) => current + (item?.working_minutes ?? 0),
      0,
    );

    setFee(coursePrice + optionPrice);
    setWorkingMinutes(courseMinutes + optionMinutes);
  }, [
    coatingItemCode,
    bodySize,
    polishOption,
    courseItems,
    optionItemCodes,
    optionItems,
  ]);

  return {
    fee,
    workingMinutes,
    formMethods,
  };
};
